import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { t } from 'i18next';
import { FormApi } from 'final-form';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { Form, Field } from 'react-final-form';
import SideDrawer from '../../../SideDrawer';
import RadioGroupField from 'components/RadioGroupField';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SideDrawerRef } from '../../../SideDrawer/SideDrawer.component';
import TextAreaField from 'components/TextAreaField';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import QAndAContext from 'router/subrouters/DataRoom/providers/QAndA/QAndA.context';
import useCallbackRef from 'hooks/useCallbackRef';
import useHeightAnimation from 'hooks/useHeightAnimation';
import { TeamInfoField } from './components';
import LinkToIcon from 'icons/LinkTo.icon';
import validations from 'validations';
import AnswerRichTextEditor from '../../../AnswerRichTextEditor';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { QuestionPreview } from '../../../QuestionPreview';
import {
  Q_AND_A_SIDES,
  QuestionManagingDrawerCommonProps,
} from 'router/subrouters/DataRoom/models/QAndA';
import useGetFollowUpQuestion from '../../../QAndABuyerContainer/hooks/useGetFollowUpQuestion';
import { PreviewSideDrawerRef } from '../../../PreviewQuestionDrawer/PreviewQuestionDrawer.component';
import {
  doesObjectHasTrutfullyValue,
  getCheckedValuesInObject,
  getDataRoomRoute,
  getJoinedWordsWithComma,
  getUserFullNameOrEmail,
} from 'utils';
import arrayMutators from 'final-form-arrays';
import AddQuestionToFaq from '../../../AddQuestionToFaq';
import { processFAQAccess } from '../../../FAQAccess/utils';
import {
  DATA_ROOM_FAQ_ACCESS,
  FAQAccessForm,
} from 'router/subrouters/DataRoom/models/FAQ';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import {
  ANSWER_REVIEW_OUTPUT,
  APPROVAL_MODUS,
  QuestionReplyInitiatedEventProps,
  QuestionReviewInitiatedEventProps,
} from 'models/MixpanelEvents';
import { richTextJsonToPlainText } from 'components/RichTextEditorField/utils';

import './ManageQuestion.styles.scss';

type ManageQuestionDrawerProps = QuestionManagingDrawerCommonProps;

type ManageQuestionDrawerForm = {
  respond?: string;
  individuals?: Record<string, boolean>;
  teams?: Record<string, boolean>;
  action: 'approveQuestion' | 'rejectQuestion' | 'createAnswer';
  answerContent?: string;
  autoApproveAnswer?: boolean;
  publishToFAQ?: boolean;
} & FAQAccessForm;

const ManageQuestionDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageQuestionDrawerProps
> = ({ question, onClose, onQuestionsUpdate, updateQuestion }, ref) => {
  const {
    isAdmin,
    isOwner,
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const { popServerError, popSuccess, popError } = useContext(
    PopNotificationsContext,
  );
  const { questionAnswererMembers, faqAccessData } = useContext(QAndAContext);

  const [isIndividual, setIsIndividual] = useState(true);
  const [container, containerRef] = useCallbackRef<HTMLDivElement>();
  const [drawer, drawerRef] = useCallbackRef<PreviewSideDrawerRef>();

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { isLoading, displayedQuestions } = useGetFollowUpQuestion(
    question,
    drawer?.open,
  );

  useHeightAnimation({
    element: container,
    defaultOpen: true,
  });

  const getMixpanelProps = useGetMixpanelProps();

  const onQuestionManage = useCallback(
    async (
      values: ManageQuestionDrawerForm,
      formApi: FormApi<
        ManageQuestionDrawerForm,
        Partial<ManageQuestionDrawerForm>
      >,
    ) => {
      try {
        const {
          action,
          individuals,
          teams,
          faqAccess,
          faqAccessStages,
          accessDataRoomBuySideMemberIds,
          answerContent,
          respond,
          publishToFAQ,
          autoApproveAnswer,
        } = values;

        const baseReviewMixpanelProps = getMixpanelProps({
          questionId: question._id,
          questionContent: richTextJsonToPlainText(question.content),
          approvalModus: APPROVAL_MODUS.MANUAL,
        });

        if (action === 'approveQuestion') {
          const individualAssign = getCheckedValuesInObject(individuals);
          const teamAssign = getCheckedValuesInObject(teams);

          const teamsAssignedIndividuals = teamAssign.reduce((acc, curr) => {
            const team = Object.entries(
              questionAnswererMembers?.dataRoomQuestionAnswerersGroupByTeams ||
                {},
            )
              .find(([key]) => key === curr)?.[1]
              .map((value) => value.userId);

            if (team) return [...acc, ...team];
            return [...acc];
          }, []);

          const assignedIds = isIndividual
            ? individualAssign
            : [...new Set(teamsAssignedIndividuals)];

          mixpanelService.questionReviewInitiated(
            getMixpanelProps({
              ...baseReviewMixpanelProps,
              questionAssignee: getJoinedWordsWithComma(assignedIds),
              questionOutput: ANSWER_REVIEW_OUTPUT.ACCEPTED,
              isOwner,
              userSide: Q_AND_A_SIDES.SELL,
            }) as QuestionReviewInitiatedEventProps,
          );

          await dataRoomApi.questionsAndAnswers.assignQuestion(
            question._id,
            assignedIds,
          );
        } else if (action === 'createAnswer') {
          mixpanelService.questionReplyInitiated(
            getMixpanelProps({
              ...baseReviewMixpanelProps,
              answerContent: richTextJsonToPlainText(answerContent),
              addedToFAQ: publishToFAQ,
              isOwner,
            }) as QuestionReplyInitiatedEventProps,
          );

          const newFaqValues = processFAQAccess(
            { faqAccess, faqAccessStages, accessDataRoomBuySideMemberIds },
            faqAccessData,
          );

          await dataRoomApi.questionsAndAnswers.createAnswer(question._id, {
            ...newFaqValues,
            publishToFAQ,
            answerContent,
            autoApproveAnswer,
          });
        } else {
          mixpanelService.questionReviewInitiated(
            getMixpanelProps({
              ...baseReviewMixpanelProps,
              response: respond,
              isOwner,
              questionOutput: ANSWER_REVIEW_OUTPUT.REJECTED,
              userSide: Q_AND_A_SIDES.SELL,
            }) as QuestionReviewInitiatedEventProps,
          );

          await dataRoomApi.questionsAndAnswers.rejectQuestion(
            question._id,
            respond,
          );
        }

        onQuestionsUpdate?.();
        onClose?.();
        popSuccess(t(`manageQuestionSellOptions.${values.action}Success`));
        formApi.restart();
      } catch (e) {
        popServerError(e);
      }
    },
    [
      isIndividual,
      question,
      faqAccessData,
      isOwner,
      questionAnswererMembers?.dataRoomQuestionAnswerersGroupByTeams,
      popServerError,
      onQuestionsUpdate,
      onClose,
      popSuccess,
      getMixpanelProps,
    ],
  );

  const individualCheckbox = useMemo(
    () =>
      questionAnswererMembers?.allDataRoomQuestionAnswerers?.length ? (
        <CheckboxFieldGroup
          className="qa-manage-question__individuals"
          options={questionAnswererMembers.allDataRoomQuestionAnswerers.map(
            (member) => ({
              value: member.userId,
              label: getUserFullNameOrEmail(member),
            }),
          )}
          name="individuals"
          direction="column"
        />
      ) : (
        t('manageQuestionSellOptions.noIndividuals')
      ),
    [questionAnswererMembers],
  );

  const teamSelect = useMemo(
    () =>
      Object.keys(
        questionAnswererMembers?.dataRoomQuestionAnswerersGroupByTeams || {},
      ).length
        ? Object.entries(
            questionAnswererMembers.dataRoomQuestionAnswerersGroupByTeams,
          )
            .filter(([_, value]) => !!value.length)
            .map(([key, value]) => (
              <Field
                key={key}
                name={`teams.${key}`}
                type="checkbox"
                teamName={key}
                members={value.map(getUserFullNameOrEmail)}
                component={TeamInfoField}
              />
            ))
        : t('manageQuestionSellOptions.noTeams'),
    [questionAnswererMembers],
  );

  const actionOptions = useMemo(
    () => [
      {
        label: t('manageQuestionSellOptions.approveQuestion'),
        value: 'approveQuestion',
      },
      ...(isAdmin
        ? [
            {
              label: t('manageQuestionBuyOptions.anwerMyself'),
              value: 'createAnswer',
            },
          ]
        : []),
      {
        label: t('manageQuestionSellOptions.rejectQuestion'),
        value: 'rejectQuestion',
      },
    ],
    [isAdmin],
  );

  return (
    <SideDrawer
      ref={drawerRef}
      closeAction={onClose}
      isLoading={isLoading}
      className="qa-manage-question"
    >
      <p className="drawer-title">{t('manageQuestion')}</p>

      <Form
        onSubmit={onQuestionManage}
        initialValues={{
          action: 'approveQuestion',
          faqAccess: DATA_ROOM_FAQ_ACCESS.ALL,
        }}
        mutators={{ ...arrayMutators }}
        render={({
          handleSubmit,
          submitting,
          invalid,
          values: { individuals, teams, action },
          form: { submit },
        }) => {
          const assigned =
            action !== 'approveQuestion' ||
            (action === 'approveQuestion' &&
              ((isIndividual && doesObjectHasTrutfullyValue(individuals)) ||
                (!isIndividual && doesObjectHasTrutfullyValue(teams))));

          return (
            <form onSubmit={handleSubmit} className="qa-manage-question__form">
              <QuestionPreview
                question={question}
                displayedQuestions={displayedQuestions}
                updateQuestion={updateQuestion}
              />

              <RadioGroupField
                className="qa-manage-question__action"
                options={actionOptions}
                name="action"
                direction="column"
              />

              <div ref={containerRef} className="textarea-container">
                {action === 'rejectQuestion' ? (
                  <Field
                    label={t('respondToQuestion')}
                    className="qa-manage-question__respond"
                    name="respond"
                    component={TextAreaField}
                    maxLength={2000}
                    required
                    validate={validations.required()}
                  />
                ) : action === 'createAnswer' ? (
                  <>
                    <AnswerRichTextEditor label={t('provideAnswer')} />
                    <Field
                      validate={validations.checkboxRequiredTrue(
                        t('confirmThisAnswer'),
                      )}
                      type="checkbox"
                      className="dr-bordered-checkbox"
                      name="autoApproveAnswer"
                      component={CheckboxField}
                      label={t('checkToConfirmThis')}
                    />
                    <AddQuestionToFaq question={question} />
                  </>
                ) : (
                  <>
                    <div className="qa-manage-question__assign">
                      <div className="qa-manage-question__assign__select">
                        <Button
                          className={classNames({ selected: isIndividual })}
                          onClick={() => setIsIndividual(true)}
                        >
                          {t('individual')}
                        </Button>
                        <Button
                          className={classNames({ selected: !isIndividual })}
                          onClick={() => setIsIndividual(false)}
                        >
                          {t('team')}
                        </Button>
                      </div>

                      {isAdmin && (
                        <Link
                          to={getDataRoomRoute(dataRoomId, 'members')}
                          className="qa-manage-question__assign__members"
                        >
                          <LinkToIcon /> {t('manageTeams')}
                        </Link>
                      )}
                    </div>

                    {isIndividual ? individualCheckbox : teamSelect}
                  </>
                )}
              </div>

              <Button
                disabled={submitting || invalid}
                onClick={() => {
                  if (assigned) {
                    submit();
                  } else popError(t('assignQuestionToSomeone'));
                }}
              >
                {t(`manageQuestionSellOptions.${action}Text`)}
              </Button>
            </form>
          );
        }}
      />
    </SideDrawer>
  );
};

export default forwardRef(ManageQuestionDrawer);
